import React, {useEffect, useState, useRef} from "react";
import dayjs from "dayjs";
import {
	Box, Button, Typography, IconButton, TextField, Paper,
	Popover, FormControl, InputAdornment, MenuItem, Select,
	Checkbox, ListItemText, InputLabel, Divider,
} from "@mui/material";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider, DatePicker} from "@mui/x-date-pickers";
import {useMediaQuery} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import OrderFilters from "./OrderFilters";
import OrderList from "./OrderList";
import {useDispatch} from "react-redux";
import {fetchOrdersByDate} from "../../store/ordersSlice";
import {ArrowBackIos, ArrowForwardIos, Refresh as RefreshIcon} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {useNavigate} from "react-router-dom";
import {
	ADVANCED_SEARCH_DEFAULT_PAGE_SIZE,
	buildAdvancedSearchRequest,
	filterAdvancedResults,
	isAdvancedSearchRequestEmpty,
	normalizeAdvancedSearchResponse,
	sanitizeAdvancedSearchInputs,
} from "./advancedSearchUtils";
import {ThemeProvider} from "@mui/material/styles";
import {buttonTheme} from "../../theme";
import {config} from "../../config";
import {getVendorId, getSessionToken} from "../../utils/authSession";

const defaultAdvancedPaging = {
	page: 0,
	pageSize: ADVANCED_SEARCH_DEFAULT_PAGE_SIZE,
	hasMore: false,
	rowCount: 0,
};


const statusOptions = [
	{uiLabel: "New", icon: "🆕", apiStatus: "NEW"},
	{uiLabel: "Processing", icon: "🔄", apiStatus: "PROCESSING"},
	{uiLabel: "Picked", icon: "✔️", apiStatus: "PICKED"},
	{uiLabel: "Packed", icon: "📦", apiStatus: "PACKED"},
	{uiLabel: "Shipped", icon: "🚚", apiStatus: "SHIPPED"},
	{uiLabel: "Out For Delivery", icon: "📍", apiStatus: "OUT_FOR_DELIVERY"},
	{uiLabel: "Delivered", icon: "✅", apiStatus: "DELIVERED"},
	{uiLabel: "Ready to Pickup", icon: "🛍️", apiStatus: "READY_FOR_PICKUP"},
	{uiLabel: "Picked Up", icon: "🙌", apiStatus: "PICKED_UP"},
	{uiLabel: "Cancelled", icon: "❌", apiStatus: "CANCELLED"},
	{uiLabel: "On Hold", icon: "⏸", apiStatus: "ON_HOLD"},
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

export default function ManageOrders() {
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:1050px)");
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [filters, setFilters] = useState({
		searchQuery: "",
		selectedStatus: [],
		selectedDate: dayjs().startOf("day"),
		orderId: "",
		orderNumber: "",
		customerName: "",
		buyerName: "",
		startDate: null,
		endDate: null,
	});


	const [searchResults, setSearchResults] = useState(null);
	const [searchLoading, setSearchLoading] = useState(false);
	const [filterAnchorEl, setFilterAnchorEl] = useState(null);
	const [filterOpen, setFilterOpen] = useState(false);

	const [filterValues, setFilterValues] = useState({
		orderId: "",
		orderNumber: "",
		customerName: "",
		buyerName: "",
		orderStatus: [],
		startDate: null,
		endDate: null,
	});

	const [showMobileSearch, setShowMobileSearch] = useState(false);
	const mobileSearchContainerRef = useRef(null);
	const popoverContentRef = useRef(null);

	const sessionToken = getSessionToken;
	useEffect(() => {
		setFilterValues((prev) => ({
			...prev,
			orderId: filters.orderId || "",
			orderNumber: filters.orderNumber || "",
			customerName: filters.customerName || "",
			buyerName: filters.buyerName || "",
			orderStatus: filters.selectedStatus || [],
			startDate: filters.startDate || null,
			endDate: filters.endDate || null,
		}));
	}, [filters]);

	useEffect(() => {
		function handleClickOutside(e) {
			const container = mobileSearchContainerRef.current;
			const popoverBox = popoverContentRef.current;
			if (container && popoverBox) {
				const clickedOutsideContainer = !container.contains(e.target);
				const clickedOutsidePopover = !popoverBox.contains(e.target);
				if (clickedOutsideContainer && clickedOutsidePopover) {
					setShowMobileSearch(false);
				}
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleFiltersChange = (newFilters) => {
		setFilters((prev) => ({...prev, ...newFilters}));
	};

	const handleRefresh = () => {
		if (filters.selectedDate) {
			dispatch(fetchOrdersByDate({
				date: filters.selectedDate.format('YYYY-MM-DD'),
				forceRefresh: true
			}));
		}
	};

	/** Attempt an advanced search (POST) */
	const handleSearch = async (inputValues = filterValues) => {
		const sanitizedValues = sanitizeAdvancedSearchInputs(inputValues);
		setFilterValues(sanitizedValues);
		setFilters((prev) => ({
			...prev,
			...sanitizedValues,
			selectedStatus: sanitizedValues.orderStatus || [],
		}));

		const requestBody = buildAdvancedSearchRequest({
			...sanitizedValues,
			selectedStatus: sanitizedValues.orderStatus,
		});

		if (isAdvancedSearchRequestEmpty(requestBody)) {
			// console.log("No advanced filters. Skipping search.");
			setSearchResults(null);
			setFilterOpen(false);
			setShowMobileSearch(false);
			return;
		}

		setSearchLoading(true);
		try {
			const vendorId = getVendorId();
			if (!vendorId) {
				throw new Error("vendorId missing");
			}
			const token = sessionToken();
			if (!token) {
				throw new Error("session token missing");
			}
			const response = await fetch(
				`${config.API_MWSSTOREFRONT_BASE_URL}/storefront/orm/${encodeURIComponent(vendorId)}/orders/search?pageNumber=1&pageSize=${ADVANCED_SEARCH_DEFAULT_PAGE_SIZE}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-Session-Token": token,
					},
					body: JSON.stringify(requestBody),
				}
			);
			if (!response.ok) {
				const errorText = await response.text().catch(() => "");
				throw new Error(`Failed to search orders (${response.status}): ${errorText}`);
			}
			const data = await response.json();
			const normalized = normalizeAdvancedSearchResponse(data);
			const filtered = filterAdvancedResults(normalized, sanitizedValues);
			setSearchResults(filtered);
		} catch (error) {
			console.error("Error searching orders:", error);
		} finally {
			setFilterOpen(false);
			setShowMobileSearch(false);
			setSearchLoading(false);
		}
	};


	const handleSearchFocus = (event) => {
		if (!filterOpen) {
			setFilterAnchorEl(event.currentTarget);
			setFilterOpen(true);
		}
	};

	const handleSearchInputClick = (event) => {
		handleSearchFocus(event);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				width: "100%",
			}}
		>
			<ThemeProvider theme={theme}>
				<Box
					sx={{
						display: "flex",
						flexDirection: isMobile ? "column" : "row",
						alignItems: "center",
						justifyContent: isMobile ? "center" : "space-between",
						gap: isMobile ? 1 : 2,
						backgroundColor: "#efefef",
						px: isMobile ? 1 : 2,
						py: isMobile ? 1 : 2,
						position: "sticky",
						top: 0,
						zIndex: 1000,
					}}
				>
					{isMobile ? (
						<Box sx={{display: "flex", flexDirection: "column", width: "100%", gap: 1}}>
							{showMobileSearch && (
								<Box sx={{width: "100%"}} ref={mobileSearchContainerRef}>
									<FormControl variant="outlined" fullWidth>
										<TextField
											placeholder="Search orders"
											onClick={handleSearchInputClick}
											inputProps={{readOnly: true}}
											variant="outlined"
											size="small"
											InputProps={{
												startAdornment: (
													<InputAdornment position="end">
														<SearchIcon/>
													</InputAdornment>
												),
											}}
										/>
									</FormControl>
								</Box>
							)}

							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									width: "100%",
									justifyContent: "space-between",
								}}
							>
								<Box sx={{display: "flex", alignItems: "center", gap: 1}}>
									{/* Mobile back: compact circular */}
									<IconButton
										onClick={() => navigate("/")}
										aria-label="Go back"
										size="small"
										sx={{
											width: 36,
											height: 36,
											borderRadius: 999,
											border: (t) => `1px solid ${t.palette.divider}`,
											backgroundColor: (t) =>
												t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
											boxShadow: (t) => (t.palette.mode === "dark" ? "none" : "0 1px 0 rgba(0,0,0,0.04)"),
											"&:hover": {backgroundColor: (t) => t.palette.action.hover},
											"&:active": {transform: "translateY(1px)"},
											transition: "transform 120ms ease",
										}}
									>
										<ArrowBackIosNewIcon fontSize="small"/>
									</IconButton>
									<ThemeProvider theme={theme}>
										<Button
											variant="outlined"
											size="small" color="secondary"
											sx={{borderRadius: 6, borderWidth: 2, "&:hover": {borderWidth: 2}}}
											onClick={() => handleFiltersChange({selectedDate: dayjs()})}
										>
											Today
										</Button>
										<IconButton
											onClick={() => {
												const prevWeek = filters.selectedDate.clone().subtract(1, "week");
												handleFiltersChange({selectedDate: prevWeek});
											}}
										>
											<ArrowBackIos sx={{fontSize: 18}}/>
										</IconButton>
										<IconButton
											onClick={() => {
												const nextWeek = filters.selectedDate.clone().add(1, "week");
												handleFiltersChange({selectedDate: nextWeek});
											}}
											disabled={filters.selectedDate.isSame(dayjs(), "week")}
										>
											<ArrowForwardIos sx={{fontSize: 18}}/>
										</IconButton>
									</ThemeProvider>
									<Typography variant="body1" sx={{fontWeight: 600}}>
										{filters.selectedDate.format("MMMM YYYY")}
									</Typography>
								</Box>

								<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
									<ThemeProvider theme={theme}>
										<IconButton onClick={handleRefresh} color="secondary">
											<RefreshIcon/>
										</IconButton>
									</ThemeProvider>
									<IconButton onClick={() => setShowMobileSearch((prev) => !prev)}>
										<SearchIcon/>
									</IconButton>
								</Box>
							</Box>
						</Box>
					) : (
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								gap: 2,
								width: "100%",
							}}
						>
							<Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
								<ThemeProvider theme={theme}>
									<Button
										onClick={() => navigate("/")}
										startIcon={<ArrowBackIosNewIcon/>}
										aria-label="Go back"
										variant="outlined"
										size="small" color="secondary"
										sx={{
											borderRadius: 999,
											textTransform: "none",
											px: 1.25,
											py: 0.75,
											fontWeight: 600,
											borderColor: "divider",
											backgroundColor: (t) =>
												t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
											boxShadow: (t) => (t.palette.mode === "dark" ? "none" : "0 1px 0 rgba(0,0,0,0.04)"),
											"&:hover": {backgroundColor: (t) => t.palette.action.hover},
										}}
									>
										Back
									</Button>

									<Button
										variant="outlined" color="secondary"
										sx={{borderRadius: 6, borderWidth: 2, "&:hover": {borderWidth: 2}}}
										onClick={() => handleFiltersChange({selectedDate: dayjs()})}
									>
										Today
									</Button>
									<IconButton
										onClick={() => {
											const prevWeek = filters.selectedDate.clone().subtract(1, "week");
											handleFiltersChange({selectedDate: prevWeek});
										}}
									>
										<ArrowBackIos sx={{fontSize: 20}}/>
									</IconButton>
									<IconButton
										onClick={() => {
											const nextWeek = filters.selectedDate.clone().add(1, "week");
											handleFiltersChange({selectedDate: nextWeek});
										}}
										disabled={filters.selectedDate.isSame(dayjs(), "week")}
									>
										<ArrowForwardIos sx={{fontSize: 20}}/>
									</IconButton>
								</ThemeProvider>
								<Typography variant="body1" sx={{fontWeight: 600}}>
									{filters.selectedDate.format("MMMM YYYY")}
								</Typography>
							</Box>

							<Typography
								variant="h5"
								color="inherit"
								sx={{fontWeight: 600, textAlign: "center", flex: 1}}
							>
								Order Management
							</Typography>

							<Box sx={{display: 'flex', alignItems: 'center', gap: 2, width: "100%", maxWidth: {md: 350, lg: 400, xl: 550}, justifyContent: 'flex-end'}}>
								<ThemeProvider theme={theme}>
									<Button
										variant="outlined"
										size="small"
										color="secondary"
										startIcon={<RefreshIcon/>}
										onClick={handleRefresh}
										sx={{px: 1, minWidth: '80px', borderWidth: 2, "&:hover": {borderWidth: 2}, whiteSpace: 'nowrap'}}
									>
										Refresh
									</Button>
								</ThemeProvider>
								<Box sx={{width: "100%"}}>
									<FormControl variant="outlined" fullWidth>
										<TextField
											placeholder="Search orders"
											onClick={handleSearchInputClick}
											inputProps={{readOnly: true}}
											variant="outlined"
											size="small"
											InputProps={{
												startAdornment: (
													<InputAdornment position="end">
														<SearchIcon/>
													</InputAdornment>
												),
											}}
										/>
									</FormControl>
								</Box>
							</Box>
						</Box>
					)}
				</Box>
				<Divider/>
				<Box
					sx={{
						display: "flex",
						flexDirection: isMobile ? "column" : "row",
						gap: 1,
						flexWrap: "nowrap",
					}}
				>
					<Box
						component={Paper}
						elevation={0}
						sx={{
							maxWidth: isMobile ? "100%" : 477,
							width: "100%",
							position: "sticky",
							top: isMobile ? 56 : 166.52,
							zIndex: 999,
						}}
					>
						<OrderFilters
							filters={filters}
							onFiltersChange={handleFiltersChange}
							onSearchResults={(data) => setSearchResults(data)}
						/>
					</Box>

					<Box sx={{flex: 1, overflowY: "auto"}}>
						<OrderList
							filters={filters}
							searchResults={searchResults}
							onAdvancedSearchResultsChange={setSearchResults}
							searchLoading={searchLoading}
						/>
					</Box>
				</Box>
				<Popover
					open={filterOpen}
					anchorEl={filterAnchorEl}
					onClose={() => {
						setFilterOpen(false);
						setShowMobileSearch(false);
					}}
					anchorOrigin={{vertical: "top", horizontal: "right"}}
					transformOrigin={{vertical: "top", horizontal: "right"}}
					PaperProps={{
						sx: {
							p: isMobile ? 1 : 2,
							borderRadius: 2,
							maxWidth: isMobile ? "calc(100% - 32px)" : 450,
							width: "100%",
							maxHeight: "70vh",
							overflowY: "auto",
						},
					}}
				>
					<Box ref={popoverContentRef}>
						<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
							Advanced Search
						</Typography>

						<Box
							display="grid"
							gridTemplateColumns="1fr"
							rowGap={isMobile ? 1 : 2}
							sx={{minWidth: "280px"}}
						>
							<TextField
								label="Order ID"
								value={filterValues.orderId}
								onChange={(e) =>
									setFilterValues({...filterValues, orderId: e.target.value})
								}
								fullWidth
								size="small"
							/>
							<TextField
								label="Order Number"
								value={filterValues.orderNumber}
								onChange={(e) =>
									setFilterValues({...filterValues, orderNumber: e.target.value})
								}
								fullWidth
								size="small"
							/>
							<TextField
								label="Customer Name"
								value={filterValues.customerName}
								onChange={(e) =>
									setFilterValues({
										...filterValues,
										customerName: e.target.value,
									})
								}
								fullWidth
								size="small"
							/>
							<TextField
								label="Buyer Name"
								value={filterValues.buyerName}
								onChange={(e) =>
									setFilterValues({
										...filterValues,
										buyerName: e.target.value,
									})
								}
								fullWidth
								size="small"
							/>
							<FormControl fullWidth size="small">
								<InputLabel>Order Status</InputLabel>
								<Select
									label="Order Status"
									multiple
									value={filterValues.orderStatus}
									onChange={(e) =>
										setFilterValues({...filterValues, orderStatus: e.target.value})
									}
									renderValue={(selected) =>
										selected
											.map((code) => {
												const match = statusOptions.find((s) => s.apiStatus === code);
												return match ? match.uiLabel : code;
											})
											.join(", ")
									}
									MenuProps={MenuProps}
								>
									{statusOptions.map((statusObj) => (
										<MenuItem
											key={statusObj.apiStatus}
											value={statusObj.apiStatus}
										>
											<Checkbox
												checked={
													filterValues.orderStatus.indexOf(statusObj.apiStatus) > -1
												}
											/>
											<ListItemText primary={statusObj.uiLabel}/>
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<Box sx={{display: 'flex', flexDirection: 'row', gap: 1}}>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker
										label="Start Date"
										value={filterValues.startDate}
										onChange={(date) =>
											setFilterValues({...filterValues, startDate: date})
										}
										maxDate={dayjs()}
										renderInput={(params) => (
											<TextField {...params} size="small" fullWidth/>
										)}
									/>
									<DatePicker
										label="End Date"
										value={filterValues.endDate}
										onChange={(date) =>
											setFilterValues({...filterValues, endDate: date})
										}
										maxDate={dayjs()}
										renderInput={(params) => (
											<TextField {...params} size="small" fullWidth/>
										)}
									/>
								</LocalizationProvider>
							</Box>
							<Box display="flex" justifyContent="space-between" gap={1}>
								<ThemeProvider theme={theme}>
									<Button
										variant="text" color="secondary"
										onClick={() => {
											setFilterOpen(false);
											setShowMobileSearch(false);
										}}
									>
										Cancel
									</Button>
								</ThemeProvider>
								<Box sx={{display: 'flex', gap: 1}}>
									<ThemeProvider theme={theme}>
										<Button
											variant="outlined" color="secondary"
											onClick={() => {
												const cleared = {
													orderId: "",
													orderNumber: "",
													customerName: "",
													buyerName: "",
													orderStatus: [],
													startDate: null,
													endDate: null,
												};
												setFilterValues(cleared);
												setFilters((prev) => ({
													...prev,
													...cleared,
													selectedStatus: [],
												}));
												setSearchResults(null);
											}}
										>
											Reset
										</Button>
										<Button
											variant="contained"
											color="primary"
											onClick={() => {
												handleSearch(filterValues);
											}}
										>
											Search
										</Button>
									</ThemeProvider>
								</Box>
							</Box>
						</Box>
					</Box>
				</Popover>
			</ThemeProvider>
		</Box>
	);
}

